import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import dayjs from "dayjs";
import { AuthModel } from "./_models";
import { setAccessTokenExpiry, setAuth } from "./AuthHelpers";
import { urlReturningTo } from "./postSignInDestination";

const REFRESH_PATH = "/authentication/refresh";
const INITIAL_REFRESH_BACKOFF_MS = 5_000;
const MAX_REFRESH_BACKOFF_MS = 60_000;

// isAuthenticationEndpoint() below matches ANY "/authentication/*" path — right
// for login/register (a 401 there just means "wrong credentials", no session
// to preserve, so hard-redirecting to /logout is harmless). But some
// "/authentication/*" endpoints are called by an ALREADY signed-in user, where
// a 401 can legitimately mean "the access token expired while this tab sat
// idle" — the same case every other authenticated call recovers from via
// silent refresh. Without this list those calls skip the refresh attempt and
// hard-navigate to /logout instead (window.location.href), which tears the
// SPA down mid-request — looking like the UI froze, not like an auth error.
const AUTHENTICATED_ENDPOINTS_UNDER_AUTH_PREFIX = [
  "/authentication/switch-organization",
  "/authentication/switch-branch",
  "/authentication/register-invited-user",
];

type RetriableRequestConfig = AxiosRequestConfig & {
  _authRetried?: boolean;
};

let inflightRefresh: Promise<AuthModel> | null = null;
let refreshBlockedUntil = 0;
let consecutiveRefreshFailures = 0;
let redirectingToLogout = false;

const getRefreshUrl = (): string =>
  `${import.meta.env.VITE_APP_API_URL}${REFRESH_PATH}`;

export const isRefreshEndpoint = (url = ""): boolean =>
  url.includes(REFRESH_PATH);

export const isAuthenticationEndpoint = (url = ""): boolean =>
  url.includes("/authentication/");

export const shouldAttemptUnauthorizedRefresh = (
  config: AxiosRequestConfig
): boolean => {
  const url = config.url ?? "";
  if (
    AUTHENTICATED_ENDPOINTS_UNDER_AUTH_PREFIX.some((path) => url.includes(path))
  ) {
    return true;
  }
  return !isAuthenticationEndpoint(url);
};

export const shouldRedirectToLogoutOnUnauthorized = (
  config: AxiosRequestConfig
): boolean => {
  const url = config.url ?? "";
  if (isRefreshEndpoint(url)) {
    // Refresh 401 means "no session" — callers decide (bootstrap vs forced sign-out).
    // Redirecting here would bounce /auth through /logout on every cold visit.
    return false;
  }
  if (
    isAuthenticationEndpoint(url) &&
    !AUTHENTICATED_ENDPOINTS_UNDER_AUTH_PREFIX.some((path) =>
      url.includes(path)
    )
  ) {
    // Login, MFA, register, forgot-password, etc. — show inline errors, not /logout.
    return false;
  }
  return true;
};

export const persistAuthTokens = (auth: AuthModel): void => {
  const expiryDate = dayjs()
    .add(auth.expires_in || 0, "seconds")
    .format("YYYY-MM-DD HH:mm:ss");

  localStorage.setItem("ZD-launcherLabelRemoved", true.toString());
  // Access JWT stays in memory only (ENG-4781). Refresh cookie is HttpOnly.
  setAccessTokenExpiry(expiryDate);
  setAuth(auth);
};

const resetRefreshBackoff = (): void => {
  consecutiveRefreshFailures = 0;
  refreshBlockedUntil = 0;
};

const scheduleRefreshBackoff = (): void => {
  consecutiveRefreshFailures += 1;
  const backoffMs = Math.min(
    INITIAL_REFRESH_BACKOFF_MS * 2 ** (consecutiveRefreshFailures - 1),
    MAX_REFRESH_BACKOFF_MS
  );
  refreshBlockedUntil = Date.now() + backoffMs;
};

export const isRefreshBackoffActive = (): boolean =>
  Date.now() < refreshBlockedUntil;

// True once a 401 has sent the browser to /logout: the session is dead (ENG-4704).
export const isRedirectingToLogout = (): boolean => redirectingToLogout;

export const redirectToLogout = (): void => {
  if (redirectingToLogout) {
    return;
  }
  // Already signing out: the Logout route clears the session and moves on to sign-in
  // with the `redirect` this URL carries. A reload here would only repeat the failed
  // refresh and lose that page (ENG-4698).
  const { pathname = "", search = "" } = window.location;
  // Routes match case-insensitively, so /LOGOUT is the sign-out page too.
  if (pathname.toLowerCase().startsWith("/logout")) {
    return;
  }

  redirectingToLogout = true;
  // A full page load: keep the page the merchant was on in the URL, so sign-in can
  // return to it; from /auth?redirect=… the redirect travels on (ENG-4698).
  window.location.href = urlReturningTo("/logout", pathname, search);
};

export const performTokenRefresh = async (): Promise<AuthModel> => {
  if (inflightRefresh) {
    return inflightRefresh;
  }

  if (isRefreshBackoffActive()) {
    throw new Error("Token refresh backoff active");
  }

  inflightRefresh = axios
    .post<AuthModel>(getRefreshUrl(), undefined, { withCredentials: true })
    .then((response) => {
      persistAuthTokens(response.data);
      resetRefreshBackoff();
      return response.data;
    })
    .catch((error: unknown) => {
      scheduleRefreshBackoff();
      throw error;
    })
    .finally(() => {
      inflightRefresh = null;
    });

  return inflightRefresh;
};

const handleUnauthorizedResponse = async (
  axiosInstance: AxiosInstance,
  error: AxiosError
): Promise<AxiosResponse | never> => {
  const config = error.config as RetriableRequestConfig | undefined;
  if (!config || !error.response || error.response.status !== 401) {
    return Promise.reject(error);
  }

  if (config._authRetried || !shouldAttemptUnauthorizedRefresh(config)) {
    if (shouldRedirectToLogoutOnUnauthorized(config)) {
      redirectToLogout();
    }
    return Promise.reject(error);
  }

  if (isRefreshBackoffActive()) {
    redirectToLogout();
    return Promise.reject(error);
  }

  try {
    await performTokenRefresh();
    config._authRetried = true;
    return axiosInstance(config);
  } catch (refreshError) {
    redirectToLogout();
    return Promise.reject(refreshError);
  }
};

export const resetTokenRefreshStateForTests = (): void => {
  inflightRefresh = null;
  refreshBlockedUntil = 0;
  consecutiveRefreshFailures = 0;
  redirectingToLogout = false;
};

export const setupUnauthorizedRefreshInterceptor = (
  axiosInstance: AxiosInstance
): void => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        return handleUnauthorizedResponse(axiosInstance, error);
      }

      return Promise.reject(error);
    }
  );
};
